import React, { FC, useState } from 'react';
import { Button, useTranslate, usePermissions } from 'react-admin';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import makeStyles from '@material-ui/core/styles/makeStyles';
import MuiTextField from '@material-ui/core/TextField';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import { HeadTransaction } from '~/types/HeadTransaction';
import { decryptSecureCode } from '~/api/headTransactions';
import { useNotify, useTimeout } from '~/hooks';

interface Props {
  transaction: HeadTransaction;
}

const DecryptSecureCodeAction: FC<Props> = ({ transaction }) => {
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.headTransactions.show.details.${key}`);

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  const clearCodeTimeout = useTimeout(() => {
    setCode('');
  }, 60 * 1000);

  const notify = useNotify();

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setCode('');
    setReason('');
  };

  const handleDecrypt = async () => {
    setLoading(true);
    try {
      const { data } = await decryptSecureCode(transaction.id, reason);
      clearCodeTimeout.start();
      setCode(data.code);
    } catch (error) {
      notify({ message: (error as any).message, type: 'error' });
    }
    setLoading(false);
    setReason('');
  };

  const classes = useStyles();

  if (!transaction.details?.secureCode) return null;
  if (!permissions?.includes('headTransaction.secureCode.decrypt')) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby='decrypt-secure-code-dialog-title'
        aria-describedby='decrypt-secure-code-dialog-description'
      >
        <DialogTitle id='decrypt-secure-code-dialog-title'>{t('decryptSecureCode')}</DialogTitle>
        <DialogContent style={{ maxWidth: 400, paddingBottom: 4 }}>
          <DialogContentText id='decrypt-secure-code-dialog-description'>
            {code ? (
              <div className={classes.codeContainer}>
                <Typography variant='h3' className={classes.code}>
                  {code}
                </Typography>
                <Alert severity='error'>
                  <div>{t('decryptSecureCodeAttention1')}</div>
                  <div>{t('decryptSecureCodeAttention2')}</div>
                </Alert>
              </div>
            ) : (
              <MuiTextField
                name='reason'
                label={t('secureCodeReason')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                rows={5}
                rowsMax={20}
                style={{ minWidth: 400 }}
                required
              />
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions className={classes.buttonsContainer}>
          {code ? (
            <Button label='ra.action.close' onClick={handleClose} />
          ) : (
            <>
              <Button label='ra.action.cancel' onClick={handleClose} />
              <Button
                label={translate('utils.confirmation.confirm')}
                onClick={handleDecrypt}
                variant='contained'
                disabled={reason.length < 1}
              >
                <CheckCircleIcon />
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <Button label={t('decryptSecureCode')} onClick={handleOpen} disabled={loading}>
        {loading ? <CircularProgress size={18} thickness={2} /> : <VisibilityIcon />}
      </Button>
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  codeContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    width: 400,
    height: 109,
  },
  buttonsContainer: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(3),
    paddingLeft: theme.spacing(3),
  },
  code: {
    marginBottom: theme.spacing(3),
  },
}));

export default DecryptSecureCodeAction;
